import { supabase } from "@/lib/supabase";
import {
  assertCanonicalInternalComplianceReviewPreviewTtl,
  assertCanonicalInternalComplianceReviewUuid,
  parseCanonicalInternalComplianceReviewPreview,
  type CanonicalInternalComplianceReviewPreview,
} from "@/data/contractor/compliance/canonicalInternalComplianceReview";
import {
  CanonicalInternalComplianceReviewClientError,
  isCanonicalInternalComplianceReviewEnabled,
  normalizeInternalComplianceReviewFunctionError,
  type InternalComplianceReviewFunctionInvoker,
} from "./individual-compliance-ops-review.service";

export const INDIVIDUAL_COMPLIANCE_OPS_REVIEW_PREVIEW_FUNCTION =
  "individual-compliance-ops-review-read-url";

export type GetInternalComplianceReviewPreviewInput = Readonly<{
  organizationId: string;
  contractorId?: string;
  evidenceId: string;
  expiresInSeconds?: number;
}>;

function defaultInvoker(
  functionName: string,
  options: Readonly<{ body: Readonly<Record<string, unknown>> }>,
) {
  return supabase.functions.invoke(functionName, options);
}

function assertOnlyPreviewKeys(input: unknown): void {
  const allowed = new Set([
    "organizationId",
    "contractorId",
    "evidenceId",
    "expiresInSeconds",
  ]);
  if (
    !input
    || typeof input !== "object"
    || Array.isArray(input)
    || Object.keys(input).some((key) => !allowed.has(key))
  ) {
    throw new CanonicalInternalComplianceReviewClientError(
      "invalid_request",
      "canonical_internal_compliance_review_preview_input_invalid",
      400,
    );
  }
}

export function createCanonicalInternalComplianceReviewPreviewClient(
  dependencies?: {
    invoke?: InternalComplianceReviewFunctionInvoker;
    enabled?: () => boolean;
  },
) {
  const invoke = dependencies?.invoke ?? defaultInvoker;
  const enabled =
    dependencies?.enabled ?? isCanonicalInternalComplianceReviewEnabled;
  return Object.freeze({
    async getPreview(
      input: GetInternalComplianceReviewPreviewInput,
    ): Promise<CanonicalInternalComplianceReviewPreview> {
      if (!enabled()) {
        throw new CanonicalInternalComplianceReviewClientError(
          "feature_disabled",
          "canonical_internal_compliance_review_feature_disabled",
          null,
        );
      }
      assertOnlyPreviewKeys(input);
      const contractorId = input.contractorId === undefined
        ? undefined
        : assertCanonicalInternalComplianceReviewUuid(input.contractorId);
      const expiresInSeconds = input.expiresInSeconds === undefined
        ? undefined
        : assertCanonicalInternalComplianceReviewPreviewTtl(
            input.expiresInSeconds,
          );
      const body = Object.freeze({
        organizationId:
          assertCanonicalInternalComplianceReviewUuid(input.organizationId),
        ...(contractorId ? { contractorId } : {}),
        evidenceId:
          assertCanonicalInternalComplianceReviewUuid(input.evidenceId),
        ...(expiresInSeconds !== undefined ? { expiresInSeconds } : {}),
      });
      const result = await invoke(
        INDIVIDUAL_COMPLIANCE_OPS_REVIEW_PREVIEW_FUNCTION,
        { body },
      );
      if (result.error) {
        throw normalizeInternalComplianceReviewFunctionError(
          result.data,
          result.error,
        );
      }
      try {
        return parseCanonicalInternalComplianceReviewPreview(result.data);
      } catch {
        throw new CanonicalInternalComplianceReviewClientError(
          "unexpected_error",
          "canonical_internal_compliance_review_preview_contract_invalid",
          null,
        );
      }
    },
  });
}
